"use client";

import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAppShell } from "@/context/AppShellContext";
import type { AppLanguage } from "@/context/app-shell-storage";
import { Tooltip } from "@/components/ui/Tooltip";

const LANGUAGES: readonly AppLanguage[] = ["ko", "en"] as const;

/**
 * Sidebar interface-language toggle.
 *
 * Lives in the sidebar (never the top bar) so the language can be switched from
 * any screen. State comes from `AppShellContext`, which is the language SSOT and
 * owns both local persistence and the server `PUT /api/v1/settings/ui`. The
 * `/settings/appearance` control writes through the same mutator, so the two
 * stay in sync; `SettingsProvider` is mounted only under `/settings/*` and must
 * not be reached from here.
 */
export function SidebarLanguageToggle({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const { language, setLanguage } = useAppShell();

  const label = (value: AppLanguage) =>
    value === "ko" ? t("language.korean") : t("language.english");

  if (collapsed) {
    // 접힌 레일은 w-16 아이콘 전용이라 세그먼트를 못 담는다.
    // 다음 언어로 넘기는 단일 버튼 + 툴팁으로 축약한다.
    const next: AppLanguage = language === "ko" ? "en" : "ko";
    return (
      <Tooltip
        label={t("Interface language")}
        description={label(next)}
        side="right"
      >
        <button
          type="button"
          onClick={() => setLanguage(next)}
          aria-label={`${t("Interface language")} — ${label(next)}`}
          className="relative flex h-11 w-11 items-center justify-center rounded-lg text-[var(--foreground)]/85 transition-colors hover:bg-[var(--background)]/60 hover:text-[var(--foreground)]"
        >
          <Languages size={19} strokeWidth={1.75} />
          <span className="absolute bottom-1 right-1 text-[13px] font-semibold uppercase tracking-tight text-[var(--muted-foreground)]">
            {language}
          </span>
        </button>
      </Tooltip>
    );
  }

  return (
    <div
      role="group"
      aria-label={t("Interface language")}
      className="mx-2 flex items-center gap-1 rounded-lg bg-[var(--muted)]/60 p-0.5"
    >
      <Languages
        size={14}
        strokeWidth={1.7}
        className="ml-1.5 shrink-0 text-[var(--muted-foreground)]"
        aria-hidden="true"
      />
      {LANGUAGES.map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => setLanguage(value)}
          aria-pressed={language === value}
          className={`flex-1 rounded-md px-2 py-1 text-[13.5px] transition-all ${
            language === value
              ? "bg-[var(--card)] font-medium text-[var(--foreground)] shadow-sm"
              : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
          }`}
        >
          {label(value)}
        </button>
      ))}
    </div>
  );
}

export default SidebarLanguageToggle;
